<template>
  <svg
    :width="width"
    :height="height"
    :view-box="[0, 0, width, height]"
    fill="white"
    xmlns="http://www.w3.org/2000/svg"
    @mouseleave="clearHoverData()"
  >
    <rect width="100%" height="100%" fill="#000000" class="background" />
    <g v-for="group in marketCarpetGroups" :key="group[0]">
      <MarketCarpetBox
        v-for="node in group[1]"
        :key="node.data.id"
        :node="node"
        :transform="`translate(${node.x0}, ${node.y0})`"
      />
    </g>
    <image :xlink:href="SCC_LOGO" height="15" x="100%" y="4" :transform="`translate(-${SCC_LOGO_WIDTH + SCC_LOGO_OFFSET})`"></image>
    <g v-if="dataState === 'loading'" opacity="0.8">
      <rect width="100%" height="100%" fill="#ffffff"></rect>
      <image :xlink:href="LOADING_ICON" :width="LOADING_ICON_SIZE" :height="LOADING_ICON_SIZE" x="50%" y="50%" :transform="`translate(-${LOADING_ICON_SIZE / 2} -${LOADING_ICON_SIZE / 2})`"></image>
    </g>
    <text
      v-if="dataState === 'error'"
      class="error"
      font-size="50"
      dominant-baseline="middle"
      text-anchor="middle"
      x="50%"
      y="50%"
      fill="#ffffff"
    >
      Unable to load MarketCarpet
    </text>
  </svg>
</template>

<script lang="ts" setup>
  import SCC_LOGO from '@/svg/scc-logo-dark.svg';
  import LOADING_ICON from '@/svg/loading-spinner.svg';
  import { computed, toRefs } from 'vue';
  import { group, hierarchy, treemap } from 'd3';
  import MarketCarpetBox from '@/components/MarketCarpetBox.vue';
  import { useMarketCarpetContext } from '@/ts/marketCarpetContext';
  import type { LeafNode, ParentNode } from '@/ts/composables/useMarketCarpetData';
  import { PADDING_OUTER, PADDING_TOP, PADDING_INNER } from '@/ts/constants/padding';

  const SCC_LOGO_WIDTH = 115;
  const SCC_LOGO_OFFSET = 10;
  const LOADING_ICON_SIZE = 120;

  const props = defineProps<{
    showSymbols: boolean;
    width: number;
    height: number;
  }>();

  const { width, height } = toRefs(props);

  const { dataState, currentNode, sizeBy, clearHoverData } = useMarketCarpetContext();

  const marketCarpetGroups = computed(() => {
    if (currentNode.value === null)
      return null;

    const treeHierarchy = hierarchy(currentNode.value);
    const sum = treeHierarchy.sum(sizeBy.value);
    const sortedHierarchy = sum.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

    const root = treemap<LeafNode | ParentNode>()
      .size([width.value, height.value])
      .paddingOuter(PADDING_OUTER)
      .paddingTop(PADDING_TOP)
      .paddingInner(PADDING_INNER)
      .round(true)(sortedHierarchy);

    return group(root, d => d.height)
  });
</script>
